import React, { useEffect, useState } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, InformationCircleIcon, CheckCircleIcon, UsersIcon } from '@heroicons/react/24/outline';
import patientService from '../../services/patientService';
import { errText } from '../../services/scheduleService';
import { parseSaId, initialsOf, ageFrom, fmtDob } from '../../utils/saId';
import { isAdmin } from '../../utils/permissions';

const TITLES = ['', 'Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof', 'Master'];
const RELATIONSHIPS = [
  { value: 'main_member', label: 'Main member (the patient)' },
  { value: 'spouse', label: 'Spouse / partner' },
  { value: 'child', label: 'Child' },
  { value: 'other', label: 'Other dependant' },
];

const blankCover = () => ({
  scheme_id: '', plan_option: '', membership_number: '', dependant_code: '00', relationship: 'main_member',
  main_member: { title: '', first_names: '', surname: '', initials: '', id_number: '' },
});

const EMPTY = {
  title: '', first_names: '', surname: '', initials: '', id_type: 'sa_id', id_number: '', date_of_birth: '', sex: '',
  cell_phone: '', email: '', street_address: '', suburb: '', city: '', postal_code: '', payer: 'medical_aid', notes: '',
  cover: blankCover(),
};

const fromPatient = (p) => ({
  ...Object.fromEntries(Object.keys(EMPTY).filter((k) => k !== 'cover').map((k) => [k, p[k] ?? EMPTY[k]])),
  cover: p.cover
    ? {
      scheme_id: String(p.cover.scheme_id),
      plan_option: p.cover.plan_option || '',
      membership_number: p.cover.membership_number,
      dependant_code: p.cover.dependant_code,
      relationship: p.cover.relationship,
      main_member: {
        title: p.cover.member_title || '',
        first_names: p.cover.member_first_names || '',
        surname: p.cover.member_surname || '',
        initials: p.cover.member_initials || '',
        id_number: p.cover.member_id_number || '',
      },
    }
    : blankCover(),
});

// The lowest two-digit dependant code nobody on the membership uses.
const freeCode = (taken) => {
  for (let i = 0; i < 100; i += 1) {
    const c = String(i).padStart(2, '0');
    if (!taken.includes(c)) return c;
  }
  return '';
};

/**
 * PatientEditor — add or edit one patient and their current medical aid cover.
 * An SA ID number fills in date of birth and sex. Typing a membership number
 * that is already on file offers the main member's details, so a family is
 * captured once.
 */
const PatientEditor = ({ patientId, schemes, onSchemesChange, onClose, onSaved, onOpenPatient }) => {
  const admin = isAdmin();
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(!!patientId);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [duplicates, setDuplicates] = useState(null);
  const [lookup, setLookup] = useState(null); // { membership, dependants } when the membership is already on file
  const [initialsEdited, setInitialsEdited] = useState(false);
  const [newScheme, setNewScheme] = useState(null);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    patientService.get(patientId)
      .then((p) => { setDetail(p); setForm(fromPatient(p)); setInitialsEdited(true); })
      .catch((e) => setErrors([errText(e)]))
      .finally(() => setLoading(false));
  }, [patientId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCover = (k, v) => setForm((f) => ({ ...f, cover: { ...f.cover, [k]: v } }));
  const setMember = (k, v) => setForm((f) => ({ ...f, cover: { ...f.cover, main_member: { ...f.cover.main_member, [k]: v } } }));

  // A valid SA ID decides date of birth and sex.
  const sa = form.id_type === 'sa_id' && form.id_number.trim() ? parseSaId(form.id_number) : null;
  useEffect(() => {
    if (!sa?.valid) return;
    setForm((f) => (f.date_of_birth === sa.dateOfBirth && f.sex === sa.sex ? f : { ...f, date_of_birth: sa.dateOfBirth, sex: sa.sex }));
  }, [sa?.valid, sa?.dateOfBirth, sa?.sex]); // eslint-disable-line react-hooks/exhaustive-deps

  const cover = form.cover;
  const others = (lookup?.dependants || []).filter((d) => d.patient_id !== patientId);
  const codeTaken = others.find((d) => d.dependant_code === cover.dependant_code);

  const checkMembership = async () => {
    if (!cover.scheme_id || !cover.membership_number.trim()) { setLookup(null); return; }
    try {
      const r = await patientService.lookupMembership(cover.scheme_id, cover.membership_number);
      setLookup(r.membership ? r : null);
    } catch {
      setLookup(null);
    }
  };

  const applyMembership = () => {
    const m = lookup.membership;
    const isMain = m.main_member_patient_id === patientId;
    setForm((f) => ({
      ...f,
      cover: {
        ...f.cover,
        plan_option: f.cover.plan_option || m.plan_option || '',
        relationship: isMain ? 'main_member' : f.cover.relationship === 'main_member' ? '' : f.cover.relationship,
        dependant_code: isMain ? f.cover.dependant_code : freeCode(others.map((d) => d.dependant_code)),
        main_member: {
          title: m.member_title || '', first_names: m.member_first_names || '', surname: m.member_surname,
          initials: m.member_initials, id_number: m.member_id_number || '',
        },
      },
    }));
  };

  const addScheme = async () => {
    try {
      const s = await patientService.addScheme(newScheme);
      onSchemesChange((list) => (list.some((x) => x.scheme_id === s.scheme_id) ? list : [...list, s].sort((a, b) => a.name.localeCompare(b.name))));
      setCover('scheme_id', String(s.scheme_id));
      setNewScheme(null);
    } catch (e) {
      setErrors([errText(e)]);
    }
  };

  const coverEntered = cover.scheme_id || cover.membership_number.trim();

  const save = async (confirmDuplicate = false) => {
    setSaving(true);
    setErrors([]);
    setDuplicates(null);
    const payload = {
      ...form,
      initials: initialsEdited ? form.initials : '',
      cover: form.payer === 'medical_aid' && coverEntered
        ? { ...cover, main_member: cover.relationship === 'main_member' ? undefined : cover.main_member }
        : null,
      confirm_duplicate: confirmDuplicate || undefined,
    };
    try {
      const r = patientId ? await patientService.update(patientId, payload) : await patientService.create(payload);
      onSaved(r.patient, r.warnings || []);
    } catch (e) {
      const d = e.response?.data;
      if (d?.code === 'possible_duplicate') setDuplicates(d.matches);
      else setErrors(d?.errors || [errText(e)]);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    setSaving(true);
    try {
      const p = await patientService.setActive(patientId, !detail.is_active);
      onSaved(p, [p.is_active ? 'Patient reactivated.' : 'Patient deactivated — hidden from searches, record kept.']);
    } catch (e) {
      setErrors([errText(e)]);
      setSaving(false);
    }
  };

  const activeSchemes = schemes.filter((s) => s.is_active || String(s.scheme_id) === cover.scheme_id);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-deep/50 p-4 backdrop-blur-sm" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className="my-6 w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-brand-deep">{patientId ? `${form.first_names} ${form.surname}`.trim() || 'Patient' : 'New patient'}</h2>
            {detail && (
              <p className="text-xs text-slate-400">
                Added {new Date(detail.created_at).toLocaleDateString('en-ZA')}{detail.created_by_name ? ` by ${detail.created_by_name}` : ''}
                {detail.updated_by_name ? ` · last changed by ${detail.updated_by_name}` : ''}
                {!detail.is_active && ' · INACTIVE'}
                {detail.review_status === 'pending_review' && ' · AWAITING REVIEW (patient-submitted)'}
              </p>
            )}
          </div>
          <button onClick={onClose} disabled={saving} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        {loading ? (
          <p className="px-6 py-10 text-center text-slate-400">Loading…</p>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); save(); }} className="flex flex-col gap-6 px-6 py-5">
            {/* Identity */}
            <Section title="Patient">
              <div className="grid grid-cols-6 gap-3">
                <Field label="Title" className="col-span-1">
                  <select className={inputCls} value={form.title} onChange={(e) => set('title', e.target.value)}>
                    {TITLES.map((t) => <option key={t} value={t}>{t || '—'}</option>)}
                  </select>
                </Field>
                <Field label="First names *" className="col-span-3">
                  <input className={inputCls} value={form.first_names} onChange={(e) => set('first_names', e.target.value)} placeholder="As on their ID" />
                </Field>
                <Field label="Initials" className="col-span-2">
                  <input
                    className={inputCls}
                    value={initialsEdited ? form.initials : initialsOf(form.first_names)}
                    onChange={(e) => { setInitialsEdited(true); set('initials', e.target.value.toUpperCase()); }}
                  />
                </Field>
                <Field label="Surname *" className="col-span-6">
                  <input className={inputCls} value={form.surname} onChange={(e) => set('surname', e.target.value)} />
                </Field>

                <Field label="Identification" className="col-span-2">
                  <select className={inputCls} value={form.id_type} onChange={(e) => set('id_type', e.target.value)}>
                    <option value="sa_id">SA ID number</option>
                    <option value="passport">Passport</option>
                    <option value="none">None</option>
                  </select>
                </Field>
                {form.id_type !== 'none' && (
                  <Field label={form.id_type === 'sa_id' ? 'ID number *' : 'Passport number *'} className="col-span-4">
                    <input className={`${inputCls} font-mono`} value={form.id_number} onChange={(e) => set('id_number', e.target.value)} maxLength={form.id_type === 'sa_id' ? 13 : 20} />
                    {sa && (sa.valid ? (
                      <span className="flex items-center gap-1 text-xs text-green-700"><CheckCircleIcon className="h-4 w-4" />Valid — born {fmtDob(sa.dateOfBirth)} ({ageFrom(sa.dateOfBirth)} y), {sa.sex === 'F' ? 'female' : 'male'}</span>
                    ) : form.id_number.replace(/\s/g, '').length >= 13 && (
                      <span className="text-xs text-red-600">{sa.error}</span>
                    ))}
                  </Field>
                )}
                <Field label="Date of birth *" className="col-span-3">
                  <input type="date" className={inputCls} value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} disabled={!!sa?.valid} />
                </Field>
                <Field label="Sex *" className="col-span-3">
                  <select className={inputCls} value={form.sex} onChange={(e) => set('sex', e.target.value)} disabled={!!sa?.valid}>
                    <option value="">Choose…</option>
                    <option value="F">Female</option>
                    <option value="M">Male</option>
                  </select>
                </Field>
              </div>
            </Section>

            {/* Contact */}
            <Section title="Contact">
              <div className="grid grid-cols-6 gap-3">
                <Field label="Cell" className="col-span-3"><input className={inputCls} value={form.cell_phone} onChange={(e) => set('cell_phone', e.target.value)} placeholder="082 123 4567" /></Field>
                <Field label="Email" className="col-span-3"><input className={inputCls} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
                <Field label="Street address" className="col-span-6"><input className={inputCls} value={form.street_address} onChange={(e) => set('street_address', e.target.value)} /></Field>
                <Field label="Suburb" className="col-span-2"><input className={inputCls} value={form.suburb} onChange={(e) => set('suburb', e.target.value)} /></Field>
                <Field label="City" className="col-span-2"><input className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} /></Field>
                <Field label="Postal code" className="col-span-2"><input className={inputCls} value={form.postal_code} onChange={(e) => set('postal_code', e.target.value)} maxLength={10} /></Field>
              </div>
            </Section>

            {/* Billing */}
            <Section title="Billing">
              <div className="mb-3 flex gap-2">
                {[['medical_aid', 'Medical aid'], ['private', 'Private (patient pays)']].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set('payer', v)}
                    className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${form.payer === v ? 'border-brand-navy bg-brand-navy text-white' : 'border-slate-300 text-slate-600 hover:border-brand-navy'}`}
                  >
                    {l}
                  </button>
                ))}
              </div>

              {form.payer === 'medical_aid' && (
                <div className="grid grid-cols-6 gap-3">
                  <Field label="Medical scheme" className="col-span-4">
                    {newScheme === null ? (
                      <div className="flex gap-2">
                        <select className={inputCls} value={cover.scheme_id} onChange={(e) => { setCover('scheme_id', e.target.value); setLookup(null); }}>
                          <option value="">Choose…</option>
                          {activeSchemes.map((s) => <option key={s.scheme_id} value={s.scheme_id}>{s.name}</option>)}
                        </select>
                        <button type="button" onClick={() => setNewScheme('')} className="shrink-0 rounded-lg px-2 text-xs font-semibold text-brand-teal hover:bg-teal-50">+ Add</button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input autoFocus className={inputCls} value={newScheme} onChange={(e) => setNewScheme(e.target.value)} placeholder="Full scheme name" />
                        <button type="button" onClick={addScheme} disabled={newScheme.trim().length < 3} className="shrink-0 rounded-lg bg-brand-navy px-3 text-xs font-semibold text-white disabled:opacity-50">Add</button>
                        <button type="button" onClick={() => setNewScheme(null)} className="shrink-0 px-1 text-xs text-slate-500">Cancel</button>
                      </div>
                    )}
                  </Field>
                  <Field label="Plan / option" className="col-span-2">
                    <input className={inputCls} value={cover.plan_option} onChange={(e) => setCover('plan_option', e.target.value)} placeholder="e.g. Classic Saver" />
                  </Field>
                  <Field label="Membership number" className="col-span-3">
                    <input className={`${inputCls} font-mono uppercase`} value={cover.membership_number} onChange={(e) => setCover('membership_number', e.target.value)} onBlur={checkMembership} />
                  </Field>
                  <Field label="Dependant code" className="col-span-1">
                    <input className={`${inputCls} font-mono`} value={cover.dependant_code} onChange={(e) => setCover('dependant_code', e.target.value)} maxLength={3} />
                  </Field>
                  <Field label="Patient is the…" className="col-span-2">
                    <select className={inputCls} value={cover.relationship} onChange={(e) => setCover('relationship', e.target.value)}>
                      <option value="">Choose…</option>
                      {RELATIONSHIPS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </Field>

                  {lookup && (
                    <div className="col-span-6 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-900">
                      <div className="flex items-start gap-2">
                        <UsersIcon className="mt-0.5 h-4 w-4 shrink-0" />
                        <div className="flex-1">
                          This membership is already on file — main member <strong>{[lookup.membership.member_title, lookup.membership.member_initials, lookup.membership.member_surname].filter(Boolean).join(' ')}</strong>
                          {lookup.membership.plan_option ? ` (${lookup.membership.plan_option})` : ''}.
                          {others.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {others.map((d) => (
                                <button key={d.patient_id} type="button" onClick={() => onOpenPatient(d.patient_id)} className="rounded bg-white px-1.5 py-0.5 text-xs text-blue-800 hover:underline">
                                  {d.dependant_code} · {d.first_names} {d.surname}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <button type="button" onClick={applyMembership} className="shrink-0 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-blue-700">Use these details</button>
                      </div>
                    </div>
                  )}
                  {codeTaken && (
                    <p className="col-span-6 flex items-center gap-1 text-xs text-red-600">
                      <ExclamationTriangleIcon className="h-4 w-4" /> {codeTaken.first_names} {codeTaken.surname} already has dependant code {codeTaken.dependant_code} on this membership.
                    </p>
                  )}

                  {cover.relationship && cover.relationship !== 'main_member' && (
                    <div className="col-span-6 rounded-lg border border-slate-200 p-3">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Main member</p>
                      <div className="grid grid-cols-6 gap-3">
                        <Field label="Title" className="col-span-1">
                          <select className={inputCls} value={cover.main_member.title} onChange={(e) => setMember('title', e.target.value)}>
                            {TITLES.map((t) => <option key={t} value={t}>{t || '—'}</option>)}
                          </select>
                        </Field>
                        <Field label="First names" className="col-span-3"><input className={inputCls} value={cover.main_member.first_names} onChange={(e) => setMember('first_names', e.target.value)} /></Field>
                        <Field label="Initials *" className="col-span-2">
                          <input className={inputCls} value={cover.main_member.initials} onChange={(e) => setMember('initials', e.target.value.toUpperCase())} placeholder={initialsOf(cover.main_member.first_names)} />
                        </Field>
                        <Field label="Surname *" className="col-span-3"><input className={inputCls} value={cover.main_member.surname} onChange={(e) => setMember('surname', e.target.value)} /></Field>
                        <Field label="ID number" className="col-span-3"><input className={`${inputCls} font-mono`} value={cover.main_member.id_number} onChange={(e) => setMember('id_number', e.target.value)} maxLength={20} /></Field>
                      </div>
                    </div>
                  )}

                  {detail?.cover_history?.length > 0 && (
                    <p className="col-span-6 flex items-start gap-1 text-xs text-slate-500">
                      <InformationCircleIcon className="h-4 w-4 shrink-0" />
                      Previous cover: {detail.cover_history.map((c) => `${c.scheme_name} ${c.membership_number}/${c.dependant_code} (to ${c.valid_to})`).join('; ')}
                    </p>
                  )}
                </div>
              )}
            </Section>

            <Section title="Notes">
              <textarea className={`${inputCls} min-h-[70px]`} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Administrative notes (not clinical notes)" />
            </Section>

            {duplicates && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <p className="font-semibold">This may already be a patient:</p>
                <ul className="mt-1 space-y-1">
                  {duplicates.map((d) => (
                    <li key={d.patient_id}>
                      <button type="button" onClick={() => onOpenPatient(d.patient_id)} className="font-semibold underline">
                        {[d.title, d.first_names, d.surname].filter(Boolean).join(' ')}
                      </button>{' '}— born {fmtDob(d.date_of_birth)}{d.id_number ? `, ID ${d.id_number}` : ''}
                    </li>
                  ))}
                </ul>
                <button type="button" onClick={() => save(true)} disabled={saving} className="mt-2 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700">
                  No — it’s a different person, create anyway
                </button>
              </div>
            )}

            {errors.length > 0 && (
              <ul className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {errors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            )}

            <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-4">
              <div>
                {admin && detail && (
                  <button type="button" onClick={toggleActive} disabled={saving} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-50">
                    {detail.is_active ? 'Deactivate patient' : 'Reactivate patient'}
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} disabled={saving} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
                <button type="submit" disabled={saving} className="rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-60">
                  {saving ? 'Saving…' : patientId ? 'Save changes' : 'Add patient'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const inputCls = 'w-full rounded-lg border border-slate-300 p-2.5 text-sm outline-none transition-shadow focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40 disabled:bg-slate-50 disabled:text-slate-500';

const Section = ({ title, children }) => (
  <section>
    <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">{title}</h3>
    {children}
  </section>
);

const Field = ({ label, className = '', children }) => (
  <label className={`flex flex-col gap-1.5 ${className}`}>
    <span className="text-[0.8rem] font-semibold text-slate-600">{label}</span>
    {children}
  </label>
);

export default PatientEditor;
